import { Component, type OnInit, computed, input, model, output, signal } from '@angular/core';
import { ButtonComponent } from '../button/button.component';
import { type CourtScheduleItem, DAY_OF_WEEK_OPTIONS } from '../court-schedule-list/court-schedule-list.model';

/** Clamps a numeric string to [min, max] and formats it as a zero-padded 2-digit string. */
function clampAndPad(value: string, min: number, max: number): string {
  const parsed = Math.round(+value);
  const clamped = Number.isFinite(parsed) ? Math.min(Math.max(parsed, min), max) : min;
  return String(clamped).padStart(2, '0');
}

@Component({
  selector: 'lib-court-schedule-form',
  standalone: true,
  imports: [ButtonComponent],
  styleUrls: ['./court-schedule-form.component.css'],
  host: {
    '[style.margin-top.px]': 'marginTop()',
    '[style.margin-bottom.px]': 'marginBottom()',
  },
  template: `
    @if (title()) {
      <p class="msa-csf__title">{{ title() }}</p>
    }
    <div class="msa-csf__grid">
      <div class="msa-csf__field">
        <label class="msa-csf__label">{{ dayLabel() }}</label>
        <select
          class="msa-csf__control"
          [value]="dayOfWeek()"
          [disabled]="submitting()"
          (change)="dayOfWeek.set($any($event.target).value)"
        >
          @for (day of days(); track day.value) {
            <option [value]="day.value">{{ day.label }}</option>
          }
        </select>
        <span class="msa-csf__feedback"></span>
      </div>
      <div class="msa-csf__field">
        <label class="msa-csf__label msa-csf__label--centered">{{ startTimeLabel() }}</label>
        <div class="msa-csf__time-group">
          <input
            type="number"
            inputmode="numeric"
            min="0"
            max="23"
            class="msa-csf__control msa-csf__time-input"
            [value]="startHour()"
            [disabled]="submitting()"
            (change)="setStartHour($any($event.target).value)"
          />
          <span class="msa-csf__time-sep">:</span>
          <input
            type="number"
            inputmode="numeric"
            min="0"
            max="59"
            class="msa-csf__control msa-csf__time-input"
            [value]="startMinute()"
            [disabled]="submitting()"
            (change)="setStartMinute($any($event.target).value)"
          />
        </div>
        <span class="msa-csf__feedback"></span>
      </div>
      <div class="msa-csf__field">
        <label class="msa-csf__label msa-csf__label--centered">{{ endTimeLabel() }}</label>
        <div class="msa-csf__time-group">
          <input
            type="number"
            inputmode="numeric"
            min="0"
            max="24"
            class="msa-csf__control msa-csf__time-input"
            [class.msa-csf__control--error]="showEndTimeError()"
            [value]="endHour()"
            [disabled]="submitting()"
            (change)="setEndHour($any($event.target).value)"
          />
          <span class="msa-csf__time-sep">:</span>
          <input
            type="number"
            inputmode="numeric"
            min="0"
            max="59"
            class="msa-csf__control msa-csf__time-input"
            [class.msa-csf__control--error]="showEndTimeError()"
            [value]="endMinute()"
            [disabled]="submitting() || endHour() === '24'"
            (change)="setEndMinute($any($event.target).value)"
          />
        </div>
        <span class="msa-csf__feedback" [class.msa-csf__error]="showEndTimeError()">
          @if (showEndTimeError()) {
            La hora fin no puede ser 00:00, usá 24:00 para medianoche
          }
        </span>
      </div>
      <div class="msa-csf__field">
        <label class="msa-csf__label">{{ priceLabel() }}</label>
        <input
          type="number"
          min="1"
          placeholder="0"
          class="msa-csf__control msa-csf__price-input"
          [class.msa-csf__control--error]="showPriceError()"
          [value]="priceCents()"
          [disabled]="submitting()"
          (input)="onPriceInput($any($event.target).value)"
        />
        <span class="msa-csf__feedback" [class.msa-csf__error]="showPriceError()">
          @if (showPriceError()) {
            El precio debe ser mayor a 0
          }
        </span>
      </div>
      <div class="msa-csf__field msa-csf__field--action">
        <lib-button
          [label]="addButtonLabel()"
          color="info"
          size="sm"
          [loading]="submitting()"
          (clicked)="onAdd()"
        />
        <span class="msa-csf__feedback"></span>
      </div>
    </div>
    <label class="msa-csf__replicate">
      <input
        type="checkbox"
        [checked]="replicateAllDays()"
        [disabled]="submitting()"
        (change)="replicateAllDays.set($any($event.target).checked)"
      />
      Aplicar este horario a todos los días (Lunes a Domingo y Festivo)
    </label>
  `,
})
export class CourtScheduleFormComponent implements OnInit {
  readonly title = input<string>();
  readonly days = input<readonly { value: string; label: string }[]>(DAY_OF_WEEK_OPTIONS);
  readonly dayLabel = input<string>('Día');
  readonly startTimeLabel = input<string>('Hora inicio');
  readonly endTimeLabel = input<string>('Hora fin');
  readonly priceLabel = input<string>('Precio / hora (COP)');
  readonly addButtonLabel = input<string>('+ Agregar horario');
  readonly marginTop = input<number>();
  readonly marginBottom = input<number>();
  /** Disables the fields and shows a spinner on the add button while a submission is in flight. */
  readonly submitting = input<boolean>(false);
  /** Initial state of the "apply to all days" checkbox. Set to false when editing a court that may already have day-specific schedules. */
  readonly defaultReplicateAllDays = input<boolean>(true);

  readonly add = output<CourtScheduleItem>();

  /** Two-way bindable — bind to the same parent property as a sibling court-schedule-list's `activeDay` to keep both in sync. */
  readonly dayOfWeek = model('lunes');
  readonly startTime = signal('08:00');
  readonly endTime = signal('10:00');
  readonly priceCents = signal(0);
  readonly showPriceError = signal(false);
  readonly showEndTimeError = signal(false);
  /** When checked, adding a range replicates it across every day option instead of just `dayOfWeek`. */
  readonly replicateAllDays = signal(true);

  readonly startHour = computed(() => this.startTime().split(':')[0] ?? '00');
  readonly startMinute = computed(() => this.startTime().split(':')[1] ?? '00');
  readonly endHour = computed(() => this.endTime().split(':')[0] ?? '00');
  readonly endMinute = computed(() => this.endTime().split(':')[1] ?? '00');

  ngOnInit(): void {
    this.replicateAllDays.set(this.defaultReplicateAllDays());
  }

  onPriceInput(value: string): void {
    this.priceCents.set(+value || 0);
    if (this.showPriceError() && this.priceCents() > 0) {
      this.showPriceError.set(false);
    }
  }

  setStartHour(value: string): void {
    const hour = clampAndPad(value, 0, 23);
    this.startTime.set(`${hour}:${this.startMinute()}`);
  }

  setStartMinute(value: string): void {
    const minute = clampAndPad(value, 0, 59);
    this.startTime.set(`${this.startHour()}:${minute}`);
  }

  setEndHour(value: string): void {
    const hour = clampAndPad(value, 0, 24);
    // 24:xx only makes sense as 24:00 (midnight) — force minutes to 0.
    const minute = hour === '24' ? '00' : this.endMinute();
    this.setEndTime(`${hour}:${minute}`);
  }

  setEndMinute(value: string): void {
    if (this.endHour() === '24') return; // 24:xx only makes sense as 24:00
    const minute = clampAndPad(value, 0, 59);
    this.setEndTime(`${this.endHour()}:${minute}`);
  }

  private setEndTime(value: string): void {
    this.endTime.set(value);
    if (this.showEndTimeError() && value !== '00:00') {
      this.showEndTimeError.set(false);
    }
  }

  onAdd(): void {
    if (this.submitting()) return;
    if (!this.startTime() || !this.endTime()) return;

    if (this.endTime() === '00:00') {
      this.showEndTimeError.set(true);
      return;
    }

    if (this.priceCents() <= 0) {
      this.showPriceError.set(true);
      return;
    }

    const targetDays = this.replicateAllDays()
      ? this.days().map((day) => day.value)
      : [this.dayOfWeek()];

    for (const dayOfWeek of targetDays) {
      this.add.emit({
        dayOfWeek,
        startTime: this.startTime(),
        endTime: this.endTime(),
        priceCents: this.priceCents(),
      });
    }
  }
}
